Vue.component('packOpeningRequestQueue', {
  template: templates.packOpeningRequestQueue,
  data() {
    return {
      requests: {
        pending: [],
        approved: [],
        declined: []
      },
      allRequests: [],
      currentFilter: 'pending',
      loading: false,
      approving: null,
      refreshInterval: null,
      lastRefreshed: null,
      channelId: null,

      // Decline modal
      showDeclineReasonModal: false,
      declineModalRequest: null,
      declineReason: '',

      // Notifications
      notificationMessage: null,
      notificationTimeout: null
    };
  },
  computed: {
    filteredRequests() {
      return this.requests[this.currentFilter] || [];
    }
  },
  mounted() {
    const c = this;

    // Get channel ID
    c.channelId = utils.cookies.get('channel_id') || window.streamerChannelId;

    // Load requests
    c.loadRequests();

    // Set up auto-refresh (30 seconds)
    c.refreshInterval = setInterval(() => {
      c.loadRequests();
    }, 30000);

    // Listen for WebSocket events about new requests
    if (window.visibilityWebSocketService) {
      visibilityWebSocketService.subscribe('packRequestSubmitted', (data) => {
        console.log('New pack request received:', data);
        c.loadRequests();
        c.showNotification('success', `New request from ${data.userName}!`);
      });
    }
  },
  beforeDestroy() {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
    }
    if (this.notificationTimeout) {
      clearTimeout(this.notificationTimeout);
    }
  },
  methods: {
    loadRequests() {
      const c = this;
      c.loading = true;

      if (!window.packRequestService) {
        console.warn('packRequestService not available');
        c.loading = false;
        return;
      }

      packRequestService.getPendingRequests({ channelId: c.channelId })
        .then(response => {
          const requests = response.data || [];

          // Organize by status
          c.requests = {
            pending: requests.filter(r => r.status === 'pending'),
            approved: requests.filter(r => r.status === 'approved'),
            declined: requests.filter(r => r.status === 'declined')
          };

          c.allRequests = requests;
          c.lastRefreshed = new Date();
          c.loading = false;
        })
        .catch(error => {
          console.error('Error loading requests:', error);
          c.loading = false;
          c.showNotification('danger', 'Failed to load requests');
        });
    },

    approveRequest(request) {
      const c = this;
      c.approving = request.id;

      packRequestService.approveRequest({
        requestId: request.id,
        channelId: c.channelId
      })
        .then(response => {
          if (response.data.success) {
            c.showNotification('success', `Request from ${request.requestedByUsername} approved!`);
            c.loadRequests();

            // Broadcast approval event via WebSocket
            if (window.visibilityWebSocketService) {
              visibilityWebSocketService.emit('packRequestApproved', {
                requestId: request.id,
                userName: request.requestedByUsername,
                timestamp: new Date().toISOString()
              });
            }
          } else {
            c.showNotification('danger', response.data.message || 'Failed to approve request');
          }
          c.approving = null;
        })
        .catch(error => {
          console.error('Error approving request:', error);
          c.approving = null;
          c.showNotification('danger', 'Error approving request');
        });
    },

    showDeclineModal(request) {
      this.declineModalRequest = request;
      this.declineReason = '';
      this.showDeclineReasonModal = true;
    },

    closeDeclineModal() {
      this.showDeclineReasonModal = false;
      this.declineModalRequest = null;
      this.declineReason = '';
    },

    declineRequest() {
      const c = this;

      if (!c.declineModalRequest) return;

      c.approving = c.declineModalRequest.id;

      packRequestService.declineRequest({
        requestId: c.declineModalRequest.id,
        channelId: c.channelId,
        reason: c.declineReason
      })
        .then(response => {
          if (response.data.success) {
            c.showNotification('success', `Request declined`);
            c.loadRequests();
            c.closeDeclineModal();

            // Broadcast decline event via WebSocket
            if (window.visibilityWebSocketService) {
              visibilityWebSocketService.emit('packRequestDeclined', {
                requestId: c.declineModalRequest.id,
                userName: c.declineModalRequest.requestedByUsername,
                reason: c.declineReason,
                timestamp: new Date().toISOString()
              });
            }
          } else {
            c.showNotification('danger', response.data.message || 'Failed to decline request');
          }
          c.approving = null;
        })
        .catch(error => {
          console.error('Error declining request:', error);
          c.approving = null;
          c.showNotification('danger', 'Error declining request');
        });
    },

    formatStatus(status) {
      return status.charAt(0).toUpperCase() + status.slice(1);
    },

    getStatusColor(status) {
      const colors = {
        pending: 'warning',
        approved: 'success',
        declined: 'danger',
        completed: 'info'
      };
      return colors[status] || 'secondary';
    },

    getPackTypeColor(packType) {
      const colors = {
        legendary: 'danger',
        ultimate: 'primary',
        ultra: 'info',
        standard: 'secondary',
        special: 'success'
      };
      return colors[packType?.toLowerCase()] || 'secondary';
    },

    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);

      if (diffMins < 1) return 'just now';
      if (diffMins < 60) return `${diffMins}m ago`;

      const diffHours = Math.floor(diffMins / 60);
      if (diffHours < 24) return `${diffHours}h ago`;

      const diffDays = Math.floor(diffHours / 24);
      if (diffDays < 7) return `${diffDays}d ago`;

      return date.toLocaleDateString();
    },

    formatTime(date) {
      if (!date) return '';
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    },

    showNotification(type, text) {
      const c = this;
      c.notificationMessage = { type, text };

      if (c.notificationTimeout) {
        clearTimeout(c.notificationTimeout);
      }

      c.notificationTimeout = setTimeout(() => {
        c.notificationMessage = null;
      }, 5000);
    },

    getNotificationIcon(type) {
      const icons = {
        success: 'bi bi-check-circle',
        danger: 'bi bi-exclamation-circle',
        warning: 'bi bi-exclamation-triangle',
        info: 'bi bi-info-circle'
      };
      return icons[type] || 'bi bi-info-circle';
    }
  }
});
